import {useCallback, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useDialogSlot} from 'UI/hooks/useDialogManager';

const DIALOG_PRIORITY = 1;

/**
 * Fallback delay before reloading anyway if the SW handover event never arrives. Keeps the
 * banner from ever getting stuck, for example when the page isn't controlled by a SW (hard reload).
 */
const RELOAD_FALLBACK_MS = 3000;

/**
 * Interval for actively checking for a new SW. Browsers only check on full page loads, but the app
 * navigates client-side and, installed as a PWA, often stays open in the background for days. One hour.
 */
const UPDATE_CHECK_INTERVAL_MS = 3_600_000;

/** States in which a new SW has already taken over (or been replaced) — only a reload is left to do. */
const SETTLED_SW_STATES: ServiceWorkerState[] = ['activating', 'activated', 'redundant'];

/**
 * Custom hook for the `AppUpdateBanner` component.
 * Detects when a new service worker is waiting to activate
 * and provides a function to trigger the update + reload.
 * Coordinates with the shared `DialogManagerProvider` (see `useDialogManager`) so it doesn't
 * overlap with other global dialogs/banners, such as the PWA install prompt.
 *
 * @returns An object containing:
 * - `applyUpdate`: Hides the banner, activates the new SW if it is still waiting and reloads the page.
 * - `hasUpdate`: Whether a new SW version is waiting and this banner is allowed to show it.
 * - `t`: The translation function from `react-i18next`, reused by the `AppUpdateBanner` component
 * so it stays limited to this single hook call (see `@nfq/component-single-hook`).
 */
export const useAppUpdateBanner = () => {
    const {t} = useTranslation();
    const [hasUpdate, setHasUpdate] = useState(false);
    const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined;

        let disposed = false;
        let stopUpdateChecks: (() => void) | undefined;

        // Serwist erzeugt `/sw.js` nur im Production-Build (`disable` in next.config.js). Im Dev-Modus
        // würde sonst eine veraltete oder fehlende Datei registriert. Früher im Dev registrierte
        // Service Worker werden entfernt, damit sie keine Requests mehr abfangen.
        if (process.env.NODE_ENV !== 'production') {
            /** Unregisters all service workers left over from earlier dev sessions. */
            const unregisterAll = async () => {
                const registrations = await navigator.serviceWorker.getRegistrations();

                await Promise.all(registrations.map(async registration => registration.unregister()));
            };

            void unregisterAll();

            return undefined;
        }

        /** Registers the SW and listens for updates. */
        const setup = async () => {
            let registration: ServiceWorkerRegistration;

            try {
                // Rejects e.g. under Google's Web Rendering Service, which blocks SW
                // registration by design — must be caught or it surfaces as an
                // unhandled rejection during crawling.
                registration = await navigator.serviceWorker.register('/sw.js');
            } catch {
                return;
            }

            /**
             * Handles a new service worker entering the waiting state.
             *
             * @param sw The waiting service worker.
             */
            const onWaiting = (sw: ServiceWorker | null) => {
                if (sw) {
                    setWaitingSW(sw);
                    setHasUpdate(true);
                }
            };

            // Check if there's already a waiting SW
            if (registration.waiting) {
                onWaiting(registration.waiting);
            }

            // Listen for new SW installations
            registration.addEventListener('updatefound', () => {
                const newSW = registration.installing;

                if (!newSW) return;

                newSW.addEventListener('statechange', () => {
                    if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
                        onWaiting(newSW);
                    }
                });
            });

            /** Asks the browser to re-fetch `/sw.js` and install it if it changed. */
            const checkForUpdate = async () => {
                try {
                    await registration.update();
                } catch {
                    // Offline or server unreachable – the next check will try again.
                }
            };

            /** Checks for an update whenever the app comes back to the foreground. */
            const onVisibilityChange = () => {
                if (document.visibilityState === 'visible') void checkForUpdate();
            };

            document.addEventListener('visibilitychange', onVisibilityChange);

            const intervalId = window.setInterval(() => {
                void checkForUpdate();
            }, UPDATE_CHECK_INTERVAL_MS);

            stopUpdateChecks = () => {
                document.removeEventListener('visibilitychange', onVisibilityChange);
                window.clearInterval(intervalId);
            };

            // The component may have unmounted while registering.
            if (disposed) stopUpdateChecks();
        };

        void setup();

        return () => {
            disposed = true;
            stopUpdateChecks?.();
        };
    }, []);

    /**
     * Activates the new service worker (if it is still waiting) and reloads the page so it runs the
     * new JS. The SW is built with `skipWaiting: true`, so it usually activates on its own right after
     * installing — its `controllerchange` has then already fired before the user clicks. Waiting for
     * that event would hang forever (the old bug: banner stuck on click), so an already settled SW
     * leads straight to a reload, and a fallback timer guarantees the reload in every other case.
     */
    const applyUpdate = useCallback(() => {
        setHasUpdate(false);

        let reloading = false;

        /** Reloads the page exactly once. */
        const reload = () => {
            if (reloading) return;

            reloading = true;
            window.location.reload();
        };

        if (!waitingSW || SETTLED_SW_STATES.includes(waitingSW.state)) {
            reload();

            return;
        }

        // Listen before triggering the handover, so the event can't be missed.
        navigator.serviceWorker.addEventListener('controllerchange', reload, {once: true});
        waitingSW.addEventListener('statechange', () => {
            if (waitingSW.state === 'activated') reload();
        });
        waitingSW.postMessage({type: 'SKIP_WAITING'});
        setTimeout(reload, RELOAD_FALLBACK_MS);
    }, [waitingSW]);

    const isActive = useDialogSlot('appUpdateBanner', DIALOG_PRIORITY, hasUpdate);

    return {
        applyUpdate,
        hasUpdate: hasUpdate && isActive,
        t
    };
};